import React from "react";
import { CalendarIcon } from "../Dashboard/icons";
import { planLabel } from "../../actions/billing";
import { statusBadgeClass } from "./statusBadge";

// GET /billing/subscription carries the plan, the cycle, the last payment and
// any change already scheduled. It does not carry a price, so the amount comes
// from the matching plan tier rather than being assumed.

const money = (amount, currency = "USD") => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return null;

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
};

const when = (value) => {
  if (!value) return "";

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

// What this plan costs per cycle, read off the tier the subscription names.
const priceFor = (plans, plan, interval) => {
  const tier = plans.find((entry) => entry.plan === plan);
  if (!tier) return null;

  return interval === "annual" ? tier.annualPrice : tier.monthlyPrice;
};

export default function BillingSummaryCard({ subscription, plans = [] }) {
  if (!subscription) {
    return (
      <section className="dash-card">
        <div className="dash-card-header">
          <h2 className="cz-card-title">Billing summary</h2>
        </div>
        <p className="dash-empty">No subscription on this workspace yet.</p>
      </section>
    );
  }

  const interval = subscription.billingInterval || "monthly";
  const annual = interval === "annual";
  const cycle = annual ? "year" : "month";

  const planPrice = priceFor(plans, subscription.plan, interval);
  const renewal = money(planPrice);
  const last = subscription.lastPayment;
  const method = subscription.paymentMethod;

  // A change already queued for the end of the period — worth saying plainly,
  // because the amount below is the one that will actually be charged.
  const pending = subscription.pendingPlan;

  return (
    <section className="dash-card">
      <div className="dash-card-header">
        <h2 className="cz-card-title">Billing summary</h2>
      </div>

      <ul className="bill-summary-list">
        <li>
          <span>{planLabel(subscription.plan)} plan</span>
          <span>{renewal ? `${renewal} / ${cycle}` : "—"}</span>
        </li>
        <li>
          <span>Billing cycle</span>
          <span>{annual ? "Annual" : "Monthly"}</span>
        </li>
        {method?.provider && (
          <li>
            <span>Payment method</span>
            <span className="bill-summary-method">
              {planLabel(method.provider)}
              {/* The same badge the plan card and the payment card use: this
                  one was green whatever the status said. */}
              {method.statusLabel && (
                <small className={statusBadgeClass(subscription.status)}>
                  {method.statusLabel}
                </small>
              )}
            </span>
          </li>
        )}
      </ul>

      {last && (
        <div className="bill-summary-totals">
          <div className="bill-summary-row">
            <span>Last payment</span>
            <span>{money(last.amount, last.currency) || "—"}</span>
          </div>
          {last.paidAt && (
            <div className="bill-summary-row">
              <span>Paid on</span>
              <span>{when(last.paidAt)}</span>
            </div>
          )}
          <div className="bill-summary-row bill-summary-row--total">
            <span>Next charge</span>
            <span>
              {last.currency && <small>{last.currency}</small>}{" "}
              {renewal || money(last.amount, last.currency) || "—"}
            </span>
          </div>
        </div>
      )}

      {pending && (
        <p className="bill-summary-pending">
          Switching to {planLabel(pending)}
          {subscription.pendingBillingInterval &&
            ` (${subscription.pendingBillingInterval})`}{" "}
          at the end of this period.
        </p>
      )}

      {subscription.nextBillingDate && (
        <div className="bill-next-billing">
          <CalendarIcon />
          <div>
            <strong>
              Your next billing date is {when(subscription.nextBillingDate)}
            </strong>
            <span>
              {renewal
                ? `You will be charged ${renewal}.`
                : "The amount depends on your plan at that date."}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
